// Links floor-plan layouts (projects.unit_layouts) to the things agents look at: unit-mix cards
// on the Layouts tab and individual units on the Availability tab. Layout names come from many
// sources ("1 Bedroom 722 sqft", "2BR Type A", "Unit D614 - Commercial Office"), so matching is
// by exact name first, then by bedroom count. Pure — shared by the server mapper and the tab.

export type MatchableLayout = {
  name: string;
  type?: string | null;
  bedrooms?: number | null;
  units?: string[];
  images?: string[];
};

const STUDIO_RE = /\bstudio\b/i;
const BEDROOMS_RE = /(\d+)\s*(?:br\b|b\/r|bhk|beds?\b|bedrooms?)/i;
const COMMERCIAL_RE = /\b(office|retail|shop|commercial|warehouse)\b/i;

export const normalizeLayoutName = (value: string | null | undefined): string =>
  (value ?? '').toLowerCase().replace(/[^a-z0-9]+/g, '');

// "Studio" → 0, "2 Bedroom", "2BR", "2 B/R", "2 Beds" → 2; anything else → null.
export const bedroomsFromLabel = (value: string | null | undefined): number | null => {
  if (!value) return null;
  if (STUDIO_RE.test(value)) return 0;
  const match = value.match(BEDROOMS_RE);
  if (!match?.[1]) return null;
  const n = Number(match[1]);
  return Number.isInteger(n) && n >= 0 && n < 20 ? n : null;
};

const isCommercial = (layout: MatchableLayout) => COMMERCIAL_RE.test(`${layout.name} ${layout.type ?? ''}`);

export const layoutBedrooms = (layout: MatchableLayout): number | null => {
  if (isCommercial(layout)) return null;
  if (typeof layout.bedrooms === 'number' && Number.isFinite(layout.bedrooms)) return layout.bedrooms;
  return bedroomsFromLabel(layout.type) ?? bedroomsFromLabel(layout.name);
};

const withImages = (layouts: readonly MatchableLayout[]) => layouts.filter((l) => (l.images?.length ?? 0) > 0);

// Layouts whose images belong on a unit-mix card ("1BR", "Studio"…): exact name match wins;
// otherwise every residential layout with the same bedroom count.
export const layoutsForUnitType = <T extends MatchableLayout>(unitType: string, layouts: readonly T[]): T[] => {
  const candidates = withImages(layouts) as T[];
  const key = normalizeLayoutName(unitType);
  const exact = candidates.filter((l) => key && [l.type, l.name].some((v) => normalizeLayoutName(v) === key));
  if (exact.length) return exact;
  const beds = bedroomsFromLabel(unitType);
  if (beds == null) return [];
  return candidates.filter((l) => layoutBedrooms(l) === beds);
};

const normalizeUnitNumber = (value: string) => value.toUpperCase().replace(/[\s_-]+/g, '');

// The floor plan to show for one unit that has none of its own: the layout listing its unit
// number, else the only residential layout with its bedroom count. Ambiguous → null, so an
// agent is never shown another type's plan.
export const floorPlanForUnit = (
  unit: { number?: string | null; bedrooms: number | null },
  layouts: readonly MatchableLayout[],
): string | null => {
  const candidates = withImages(layouts);
  if (unit.number) {
    const wanted = normalizeUnitNumber(unit.number);
    const listed = candidates.find((l) => l.units?.some((u) => normalizeUnitNumber(u) === wanted));
    if (listed) return listed.images?.[0] ?? null;
  }
  if (unit.bedrooms == null) return null;
  const sameBeds = candidates.filter((l) => layoutBedrooms(l) === unit.bedrooms);
  return sameBeds.length === 1 ? (sameBeds[0]?.images?.[0] ?? null) : null;
};
